/* Starting and leaving — the derived half.
 *
 * Pure, for the same reason registers.ts is: this is the logic the Onboarding
 * screen is actually made of, and a test can only reach it if it does not need
 * a tenant, a React tree or a token to run.
 *
 * ALMOST NOTHING HERE IS STORED
 *
 * The register already knows whether somebody has a depot, a laptop, a level
 * and a folder. Writing "step 3 done" beside facts that already answer step 3
 * is how the two drift, and then the flow starts lying — the screen says done,
 * the register says the laptop was never assigned, and only one of them is
 * right. So the steps are computed every time from what is there.
 *
 * Four things genuinely cannot be derived, and those are the four stamps on the
 * person: Onboarded, OffboardedOn, WelcomeSentOn and LeavingOn. Each exists
 * because no amount of looking at the register answers it — see models/index.ts.
 */

import type { HubLevel } from '@hird/access';
import type { IDevice, IPerson } from '../models';
import { isLeaver, isMissingFromSource, isRealPerson, isUnclassified, offboardingGaps } from './registers';

/* ─────────────────────────── Who is in a queue ─────────────────────────── */

/** Waiting to be set up.
 *
 *  Unclassified accounts are included deliberately. The sync creates a row for
 *  every new account in the tenant, so this queue catches the new scanner and
 *  the new shared mailbox alongside the new receptionist — and that is the
 *  point, because somebody has to say which it is and nothing else asks them.
 *  Classifying one as anything other than a person takes it straight back out.
 */
export function isOnboarding(
  person: Pick<IPerson, 'kind' | 'status' | 'onboarded' | 'leavingOn' | 'missingSince'>,
): boolean {
  if (person.onboarded) return false;
  if (isOffboarding(person)) return false; // Somebody leaving is not a starter.
  return isRealPerson(person) || isUnclassified(person);
}

/** On their way out, and not yet wound down.
 *
 *  Three ways in, and NONE of them reads `onboarded` or a joiner case. Leaving
 *  is a fact about the account in Entra (or a date somebody typed), never about
 *  how the person was set up: somebody added by hand, backfilled in October or
 *  taken through Add a joiner leaves by exactly the same door.
 *
 *  1. Status is Leaver: sign-in was blocked and the sync saw it.
 *  2. `leavingOn` is set: the date somebody typed in advance. The status is the
 *     late answer, because it only turns to Leaver once the account is already
 *     disabled, which is after the last day, not before it.
 *  3. The account has been DELETED in Entra. This is the one the other two miss:
 *     the sync only works out a status for accounts Entra still returns, so a
 *     deleted account keeps whatever status it had (Active) and the sync can do
 *     nothing but stamp `missingSince`. Before this rule a leaver whose account
 *     was deleted rather than disabled never reached this queue at all.
 *
 *  A deleted account counts only for a person or an unclassified row. A shared
 *  mailbox or service account that disappears is a different job (is anything
 *  still using it?), not somebody leaving.
 */
export function isOffboarding(
  person: Pick<IPerson, 'kind' | 'status' | 'offboardedOn' | 'leavingOn' | 'missingSince'>,
): boolean {
  if (person.offboardedOn) return false;
  if (isLeaver(person) || !!person.leavingOn) return true;
  return isMissingFromSource(person) && (isRealPerson(person) || isUnclassified(person));
}

/* ─────────────────────────── Incomplete records ─────────────────────────── */

export type RecordGapKind = 'depot' | 'start' | 'manager';

export interface RecordGap {
  kind: RecordGapKind;
  detail: string;
}

/** What the register still does not know about somebody who has been set up.
 *
 *  Separate from the onboarding queue on purpose. Everybody who was already on
 *  the register when this screen was built was stamped as onboarded, so their
 *  gaps would otherwise be invisible — and they are the gaps most worth fixing,
 *  because they have been there longest.
 *
 *  Gated the same way retentionDue and offboardingGaps are, and for the same
 *  reason: a scanner has no start date and never will, and a worklist that
 *  counts that as missing data stops being read. A leaver is excluded too —
 *  chasing a start date for somebody who left in March is work nobody should
 *  be given.
 *
 *  A mobile number is NOT a gap. It is not compulsory at Hird, plenty of people
 *  genuinely have none, and listing every one of them buried the records that
 *  really were incomplete. The same reason it does not gate onboarding.
 */
export function recordGaps(
  person: Pick<IPerson, 'kind' | 'status' | 'loc' | 'start' | 'mgr' | 'offboardedOn' | 'leavingOn' | 'missingSince'>,
): RecordGap[] {
  if (!isRealPerson(person)) return [];
  if (isOffboarding(person) || person.status === 'Leaver') return [];
  return detailGaps(person);
}

/** The same blanks, without the gate.
 *
 *  Split out because the onboarding flow needs them for somebody the gate would
 *  reject — an unclassified account on its way to being classified is exactly
 *  who the details step is for. Calling recordGaps with invented values to get
 *  past its own gate would have been the alternative, and a helper that has to
 *  be lied to is a helper in the wrong shape.
 */
function detailGaps(person: Pick<IPerson, 'loc' | 'start' | 'mgr'>): RecordGap[] {
  const gaps: RecordGap[] = [];
  /* An em dash counts as blank. The register is full of them — it is what gets
     typed into a field that does not apply, and treating it as a value would
     report a mobile number of "—" as present. */
  const blank = (v: string | undefined): boolean => {
    const s = (v ?? '').trim();
    return s === '' || s === '—' || s === '-';
  };

  if (blank(person.loc)) gaps.push({ kind: 'depot', detail: 'no depot' });
  if (blank(person.start)) gaps.push({ kind: 'start', detail: 'no start date' });
  if (blank(person.mgr)) gaps.push({ kind: 'manager', detail: 'no manager' });
  return gaps;
}

/** What the live directory read came back with, including not having worked.
 *
 *  Three states that used to be one. `level` absent with no `problem` is still
 *  in flight; a `problem` is a read that failed and will not fix itself; a
 *  `level` of null is a genuine answer meaning no hub access at all.
 */
export interface AccessReading {
  level?: HubLevel | null;
  /** Why the read could not be made, in words. */
  problem?: string;
}

function accessStep(access: AccessReading): LifecycleStep {
  if (access.problem) {
    return {
      id: 'access',
      label: 'Access',
      state: 'blocked',
      detail: access.problem,
    };
  }
  if (access.level === undefined) {
    return {
      id: 'access',
      label: 'Access',
      state: 'todo',
      detail: 'Checking what they can reach…',
    };
  }
  return {
    id: 'access',
    label: 'Access',
    state: access.level === null ? 'todo' : 'done',
    detail: access.level === null
      ? 'No hub level — they cannot sign in to the hub or any app yet'
      : `Level ${access.level}`,
  };
}

/* ─────────────────────────── The steps ─────────────────────────── */

export type StepState = 'done' | 'todo' | 'blocked' | 'n/a';

export interface LifecycleStep {
  id: string;
  label: string;
  state: StepState;
  /** What is true now — shown under the label, so the state is never a bare word. */
  detail: string;
}

/** Everything the onboarding flow will do, in the order it does it.
 *
 *  Steps arrive as the stages that implement them land, rather than appearing
 *  early as rows that cannot be pressed: a step that is permanently "coming
 *  soon" teaches people to ignore the list, which is the one thing this screen
 *  cannot afford.
 */
export function onboardingSteps(
  person: Pick<IPerson, 'kind' | 'loc' | 'mobile' | 'start' | 'onboarded' | 'welcomeSentOn'>,
  devices: readonly Pick<IDevice, 'user'>[] = [],
  personId?: string,
  /** What the live directory read came back with. */
  access: AccessReading = {},
): LifecycleStep[] {
  const steps: LifecycleStep[] = [];

  steps.push({
    id: 'classify',
    label: 'What kind of account is this?',
    state: isUnclassified(person) ? 'todo' : 'done',
    detail: isUnclassified(person)
      ? 'Nobody has said whether this is a person, a shared mailbox or a service account'
      : `Recorded as ${person.kind}`,
  });

  /* Only a person gets the rest. Classifying a scanner is the whole of its
     onboarding, which is why step one is not ceremony. */
  if (!isRealPerson(person)) {
    if (!isUnclassified(person)) {
      steps.push({
        id: 'not-a-person',
        label: 'Nothing else to do',
        state: 'n/a',
        detail: `A ${String(person.kind).toLowerCase()} has no depot, no laptop and no welcome email`,
      });
    }
    return steps;
  }

  /* Manager is left out: it is sync-owned, read from Entra, and nobody filling
     in this form can supply it. Listing it as outstanding would be asking for
     something the screen cannot accept. */
  /* Depot and start date, and nothing else. A mobile number is not compulsory
     at Hird and is not asked for — it held the flow shut for a starter who was
     otherwise ready, behind a number nobody was going to supply. The depot
     names the person's personal folder, and a blank start date is not a date
     nobody knew, it is a field nobody filled in. */
  const missing = detailGaps({ ...person, mgr: 'n/a' }).filter((g) => g.kind !== 'manager');
  steps.push({
    id: 'details',
    label: 'Their details',
    state: missing.length === 0 ? 'done' : 'todo',
    detail: missing.length === 0
      ? 'Depot and start date are both recorded'
      : missing.map((g) => g.detail).join(', '),
  });

  /* Access sits between details and equipment because it is the step that
     actually lets somebody in. A starter with a laptop and no hub level has
     nothing to use it for.

     ALWAYS pushed. It used to appear only once the live read had resolved,
     which meant a failed read, a row the sync has never matched, and a request
     still in flight all rendered identically: no step at all. That reads as a
     missing feature rather than a problem, and it is the same fault the
     dashboard warns about — unknown is not a blank, it is a confident wrong
     answer. */
  steps.push(accessStep(access));

  const theirs = personId ? devices.filter((d) => d.user === personId).length : 0;
  steps.push({
    id: 'equipment',
    label: 'Equipment',
    state: theirs > 0 ? 'done' : 'todo',
    detail: theirs > 0
      ? `${theirs} device${theirs === 1 ? '' : 's'} assigned`
      : 'Nothing assigned yet — skip this if their kit has not arrived',
  });

  /* The folder and the welcome email, which func-hird-onboarding does because
     neither is Blueprint's to do.

     WelcomeSentOn rather than "has a folder": the folder is created on the
     first press and the email goes with it, but a 207 means the folder was
     made and the email was not — and the Function only ever emails on a NEW
     folder, so that email can never be sent by pressing again. A step that
     read the folder would call that done. */
  steps.push({
    id: 'folder',
    label: 'Folder and welcome email',
    state: person.welcomeSentOn ? 'done' : 'todo',
    detail: person.welcomeSentOn
      ? `Welcomed ${whenWelcomed(person.welcomeSentOn)}`
      : 'No welcome email recorded — they have nowhere to file anything and have not been told how to sign in',
  });

  return steps;
}

/** Winding somebody down, in the order it has to happen.
 *
 *  Access first. It is the half that locks them out, and if everything after it
 *  fails the account is still shut — which is the opposite priority to
 *  onboarding, where the folder matters most. offboardAccess.ts in the hub
 *  makes the same argument for the same reason.
 *
 *  THIS LIST IS NOT THE WHOLE JOB, AND SAYS SO
 *
 *  Blueprint can take two of the three layers the hub's leaver removes. The
 *  third — a direct grant on a department's document library, and membership of
 *  a department group — needs the live permissions on each library read one at
 *  a time, and Blueprint has neither the department list nor the helpers. The
 *  licence needs User.ReadWrite.All, which this feature does not justify, and a
 *  SharePoint site group cannot be touched by any API at all.
 *
 *  So the last step is an honest hand-off rather than a tick. A flow that
 *  closed while those were outstanding would be worse than no flow: somebody
 *  would press Finish believing it was done.
 */
export function offboardingSteps(
  person: Pick<IPerson, 'kind' | 'status' | 'lic' | 'groups' | 'mfa' | 'keepUntil' | 'offboardedOn' | 'missingSince'>,
  devices: readonly Pick<IDevice, 'user'>[] = [],
  personId?: string,
  access: AccessReading = {},
): LifecycleStep[] {
  const steps: LifecycleStep[] = [];
  const theirs = personId ? devices.filter((d) => d.user === personId).length : 0;

  /* Same reasoning as the starter's access step: a read that failed must say
     so. Silently dropping "take their access away" from a leaver's list is the
     one omission on this screen that could actually hurt somebody. */
  const revoke = accessStep(access);
  steps.push(
    /* Checked first. A deleted account cannot be read, so without this the step
       would report "could not read their access" for ever and say nothing about
       the thing that is actually true: the account is gone, and its sign-in,
       licence, mailbox and group membership went with it. */
    isMissingFromSource(person)
      ? {
          id: 'revoke',
          label: 'Take their access away',
          state: 'done',
          detail: `Their account was deleted in Microsoft 365 on ${person.missingSince}. Sign-in, licence, mailbox and groups went with it`,
        }
      : revoke.state === 'blocked'
      ? { ...revoke, id: 'revoke', label: 'Take their access away' }
      : {
          id: 'revoke',
          label: 'Take their access away',
          state: access.level === null ? 'done' : 'todo',
          detail: access.level === null
            ? 'No hub level and no app groups'
            : `Still Level ${access.level} — they can sign in to the hub and its apps`,
        },
  );

  steps.push({
    id: 'kit',
    label: 'Equipment back',
    state: theirs === 0 ? 'done' : 'todo',
    detail: theirs === 0
      ? 'Nothing signed out to them'
      : `${theirs} device${theirs === 1 ? '' : 's'} still signed out`,
  });

  /* Not whether a folder exists — it always will, and it always should. What
     changes is whether THEY can still reach it. The Function is the only thing
     that knows, so this cannot be derived and is a button rather than a state. */
  steps.push({
    id: 'folder',
    label: 'Close their folder',
    state: 'todo',
    detail: 'Removes their access. The folder and everything in it stays — a contract and a payslip outlive the employment',
  });

  /* Hird's decision, not a fact, which is why the sync cannot write it and
     blank means the default window rather than "nobody has decided". */
  steps.push({
    id: 'retention',
    label: 'How long to keep the account',
    state: person.keepUntil ? 'done' : 'todo',
    detail: person.keepUntil
      ? `Keep until ${person.keepUntil}`
      : 'Blank means the default ninety days after they left',
  });

  const elsewhere = offboardingGaps(person, theirs).filter((g) => g.kind !== 'device');
  steps.push({
    id: 'elsewhere',
    label: 'Still to do outside Blueprint',
    state: elsewhere.length === 0 ? 'done' : 'blocked',
    detail: elsewhere.length === 0
      ? 'Nothing Blueprint can see is outstanding — department libraries are still worth checking in Hub Admin'
      : elsewhere.map((g) => g.detail).join(', '),
  });

  return steps;
}

/** Whether the leaver flow may be closed.
 *
 *  Access and equipment only. The licence, the department libraries and the
 *  SharePoint site groups are all outside Blueprint, and refusing to close
 *  until somebody has done them somewhere else would mean the record never
 *  said a leaver had been dealt with — so the step stays visible and the flow
 *  still closes. What it must not do is close while they can still sign in.
 */
export function canFinishOffboarding(steps: readonly LifecycleStep[]): boolean {
  const required = ['revoke', 'kit'];
  /* 'blocked' is not 'done'. If the directory could not be read we do not know
     whether they still have access, and closing a leaver on a guess is the one
     thing this flow exists to prevent. */
  return steps
    .filter((s) => required.indexOf(s.id) >= 0)
    .every((s) => s.state === 'done');
}

/** Still to be welcomed.
 *
 *  Separate from the onboarding queue because it is a different job done at a
 *  different time: everybody was given hub access at once, and the welcomes go
 *  out by hand over the following days. Somebody can be entirely set up and
 *  still not have been told the hub exists.
 *
 *  Only real people, and only ones who can actually sign in — welcoming
 *  somebody with no hub level would send them instructions for a door that is
 *  locked.
 */
export function needsWelcome(
  person: Pick<IPerson, 'kind' | 'status' | 'welcomeSentOn' | 'offboardedOn' | 'leavingOn' | 'missingSince'>,
  level?: HubLevel | null,
): boolean {
  if (person.welcomeSentOn) return false;
  if (!isRealPerson(person)) return false;
  if (isOffboarding(person)) return false;
  /* undefined means the level has not been read. Treated as "yes" so a row is
     never silently dropped from the list by a lookup that has not happened —
     the button itself is what refuses, and it says why. */
  return level !== null;
}

/** When somebody was welcomed, as a person would say it.
 *
 *  Date AND time, which is the whole reason this one stamp keeps its time: the
 *  question while working through a list of ninety-five is "did I already do
 *  this one", and "today" cannot answer it.
 */
export function whenWelcomed(iso: string | undefined): string {
  if (!iso) return 'not yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  /* en-GB, because 07/10 and 10/07 are different days and the register is read
     by people who write the day first. */
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** Whether the flow may be closed.
 *
 *  Classification and details only. Equipment, access and the welcome email can
 *  all be outstanding for good reasons — the laptop arrives next week, the
 *  account is not licensed yet — and a flow that refuses to close until every
 *  row is green is one people work around by not using it. What it must never
 *  do is close ITSELF: `onboarded` is stamped because somebody decided, not
 *  because the rows happened to all go green, which would quietly finish a case
 *  that half-failed.
 */
export function canFinishOnboarding(steps: readonly LifecycleStep[]): boolean {
  const required = ['classify', 'details'];
  return steps
    .filter((s) => required.indexOf(s.id) >= 0)
    .every((s) => s.state === 'done' || s.state === 'n/a');
}

/* ───────────────── Already set up: the manual override ───────────────── */

/** Can this person be marked as already set up, and if not, why not in words.
 *
 *  THE THREE WAYS SOMEBODY BECOMES "ONBOARDED"
 *
 *  `onboarded` is one fact: this person is in the systems and out of the queue.
 *  There are three honest routes to it and they must not be confused:
 *
 *  1. Existing staff. Stamped in bulk by Backfill-LedgerLifecycle.ps1 on 7 Oct
 *     because they were here before the flow was. Nothing to do.
 *  2. A new joiner taken through Add a joiner. IT hands over, HR and Accounts
 *     run their stages, and completing the case provisions the person
 *     (SignIn, Training Matrix, EZ Leave) and stamps them.
 *  3. This: somebody whose accounts were made by hand outside the process. It
 *     stamps and creates NOTHING, because everything it would create already
 *     exists, and doing it twice is how a person ends up with two EZ Leave
 *     records.
 *
 *  Classification is still required, because it is the one fact no route
 *  supplies on its own: stamping a row nobody has described would hide an
 *  unclassified account in the Completed list. Details are NOT required. A
 *  missing start date does not stop somebody being set up by hand, and the
 *  Incomplete records lens keeps listing the gap, which is the same bargain the
 *  October backfill made.
 *
 *  An open Onboarding case blocks it. Accounts pressing Complete runs the
 *  provisioning, so stamping the person here would leave a live case that goes
 *  on to create everything the override was meant to skip.
 */
export function canMarkSetUp(
  person: Pick<IPerson, 'kind' | 'status' | 'onboarded' | 'offboardedOn' | 'leavingOn' | 'missingSince'>,
  openCase = false,
): { ok: boolean; why: string } {
  if (person.onboarded) return { ok: false, why: 'They are already marked as set up.' };
  if (isOffboarding(person)) return { ok: false, why: 'They are leaving, not starting.' };
  if (isUnclassified(person)) {
    return { ok: false, why: 'Say what kind of account this is first. Nothing else can tell the register.' };
  }
  if (openCase) {
    return {
      ok: false,
      why: 'They have an open case with HR or Accounts. Finishing that case sets them up; marking them here as well would leave it open.',
    };
  }
  return { ok: true, why: '' };
}

/** What marking somebody as already set up writes, and nothing more.
 *
 *  The stamp, and a line in Notes. Notes because it is the one record a person
 *  can write here that survives: the audit list is written by the sync's
 *  identity and the browser's attempt is swallowed by design, so an audit entry
 *  from this action would silently not exist. The wind-down already records
 *  itself this way.
 */
export function markSetUpPatch(
  person: Pick<IPerson, 'notes'>,
  today: string,
  reason = '',
): { onboarded: string; notes: string } {
  const why = reason.trim();
  const line =
    `Marked as already set up ${today}: accounts were made by hand outside the onboarding process, ` +
    `nothing was created${why ? `. ${why}` : '.'}`;
  return {
    onboarded: today,
    notes: [person.notes, line].filter(Boolean).join('\n'),
  };
}

/** The closing stamp when IT runs Add a joiner to the end and bypasses HR and Accounts.
 *
 *  Same two writes as markSetUpPatch, different sentence: here Blueprint DID do its part (details,
 *  access, kit, perhaps the folder), and what was skipped is HR's forms and Accounts' payroll stage. */
export function bypassHrPatch(
  person: Pick<IPerson, 'notes'>,
  today: string,
): { onboarded: string; notes: string } {
  const line = `Onboarded by IT in Blueprint ${today} without HR or Accounts: no case was opened.`;
  return { onboarded: today, notes: [person.notes, line].filter(Boolean).join('\n') };
}

/* ------------------- Two lists, one fact ------------------- */

/** Does the device's own pointer agree with the open assignment row?
 *
 *  AssignedTo and Blueprint_Assignments hold the same fact in two places, which
 *  is a deliberate trade — the pointer is what three screens read and the
 *  history is what answers who had it in March — but it means they can come
 *  apart. devices.assign() writes them in an order chosen so a half-failure
 *  leaves the pointer right and a history row missing, never the reverse, and
 *  it reports what it could not do. That report is read once, by whoever
 *  pressed the button.
 *
 *  This is the version that keeps being true afterwards. Shown on the device
 *  rather than logged, because a disagreement nobody can see is one that gets
 *  believed by whichever screen is asked first.
 */
export function historyDisagrees(
  device: Pick<IDevice, 'id' | 'user'>,
  assignments: readonly { device: string; person: string; to?: string }[],
): string | null {
  const open = assignments.filter((a) => a.device === device.id && !a.to);

  if (open.length > 1) {
    return `${open.length} people are recorded as holding this at once. The history is wrong.`;
  }
  const holder = open[0];

  if (device.user && !holder) {
    return 'Assigned to somebody, but no record of when they got it.';
  }
  if (!device.user && holder) {
    return 'Not assigned to anybody, but the history still has somebody holding it.';
  }
  if (device.user && holder && holder.person !== device.user) {
    return 'The history names a different holder than the device does.';
  }
  return null;
}

/* ─────────────────────────── Level, from last night ─────────────────────── */

/** Somebody's hub level, read out of the synced group membership.
 *
 *  The queue shows a level for every row, and asking Entra for each of them
 *  would be a request per person on every render. The nightly sync already
 *  wrote the membership of every group into the register, and IGroupMember.id
 *  joins to IPerson.graphId, so the answer is already here.
 *
 *  It is AS AT THE LAST SYNC and the screen must say so. Anything about to
 *  write a level re-reads it live first — a stale read is fine for a badge and
 *  not fine for a decision.
 */
export function levelFromSyncedGroups(
  person: Pick<IPerson, 'graphId'>,
  groups: readonly { groupId: string; members: readonly { id: string }[] }[],
  levelGroups: Readonly<Record<number, string | undefined>>,
): number | null {
  if (!person.graphId) return null;
  let best: number | null = null;
  for (const [level, groupId] of Object.entries(levelGroups)) {
    if (!groupId) continue;
    const group = groups.find((g) => g.groupId === groupId);
    if (!group) continue;
    if (!group.members.some((m) => m.id === person.graphId)) continue;
    const n = Number(level);
    if (best === null || n > best) best = n;
  }
  return best;
}
